// Payment Posting data layer.
// REAL data comes from the EOB module (/eobs/files + /files/{id}/analysis).
// The posting layer (applied/unapplied, method, mode, posting rules, history,
// by-patient ledger, apply/post actions) has NO API yet — those fields/actions
// are DERIVED or MOCKED here and clearly flagged. See "API under development".
import { listEobFiles, getEobFile } from "@/lib/eobEraV2";

export const money = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const moneyShort = (v) =>
  v == null || isNaN(Number(v))
    ? "$0"
    : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

// Live processing status straight from the API file entry (mirrors the EOB→ERA flow).
export const STAGE_LABELS = {
  queued: "Queued", ocr: "Reading the document (OCR)", classifying: "Classifying document",
  exploding: "Splitting pages", extracting: "Extracting claims & lines", finalizing: "Finalizing", done: "Extraction done",
};
export function liveStatus(proc) {
  if (!proc) return null;
  if (proc.auditStatus === "complete")
    return { label: `Completed · Audit ${proc.claimCount != null ? proc.claimCount + " " : ""}✓`, sub: "All checks passed — assembling your ERA." };
  if (proc.auditStatus === "auditing" || (proc.status === "processed" && proc.auditStatus) || proc.status === "processed")
    return { label: "Under audit", sub: "Auditing the extracted claims for accuracy…" };
  if (proc.status === "processing" || proc.status === "queued")
    return { label: STAGE_LABELS[proc.stage] || "Processing", sub: proc.message || "revQ is reading your EOB…" };
  return null;
}

const hashInt = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

// ── MOCK: posting-only attributes not present on any endpoint ──
const METHODS = ["Check", "EFT", "VCP"];
const ADDED_BY = ["support", "mercury", "paramehi"];

// Map a raw /eobs/files entry → the Payment Posting "check" model.
export function toCheck(f) {
  const s = f.extraction_summary || f.summary || {};
  const amount = Number(s.check_amount ?? s.total_paid) || 0;
  const era = f.era_status;
  const processing = f.status === "processing" || f.status === "queued";
  const failed = f.status === "failed" || f.status === "error";

  // Derived posting state (heuristic mock — no posting API):
  let postState, applied, mode;
  if (processing) { postState = "processing"; applied = 0; mode = "Processing"; }
  else if (failed) { postState = "gap"; applied = 0; mode = "Extraction failed"; }
  else if (era === "era_generated") { postState = "applied"; applied = amount; mode = "ERA · posted"; }
  else if (era === "era_validation_failed") { postState = "gap"; applied = 0; mode = "ERA · no match"; }
  else if (f.status === "processed") { postState = "unapplied"; applied = 0; mode = "ERA · auto-matched"; }
  else { postState = "unapplied"; applied = 0; mode = "Processing"; }

  const h = hashInt(f.file_id || s.check_number || f.filename || "x");
  return {
    id: f.file_id,
    filename: f.filename || "—",
    payer: s.payer_name || "—",
    ediPayerId: f.edi_payer_id || s.edi_payer_id || s.payer_id || "—",
    checkNo: s.check_number || "—",
    checkDate: s.check_date || s.payment_date || f.created_at || f.uploaded_at || null,
    depositDate: null, // MOCK: no API field
    method: METHODS[h % METHODS.length], // MOCK
    addedBy: ADDED_BY[h % ADDED_BY.length], // MOCK
    mode, // partly derived / MOCK
    claims: s.claim_count ?? null,
    amount,
    applied, // MOCK-derived
    unapplied: Math.max(0, amount - applied), // MOCK-derived
    postState, // applied | unapplied | partial | gap | force | processing
    status: f.status,
    eraStatus: era,
    createdAt: f.created_at || f.uploaded_at,
    // Live processing status fields straight from the API entry.
    proc: { status: f.status, stage: f.progress?.stage, message: f.progress?.message, auditStatus: f.audit_status, claimCount: s.claim_count },
    raw: f,
  };
}

export async function listChecks({ since = "all", signal } = {}) {
  const files = await listEobFiles({ since, signal });
  return files.map(toCheck);
}

// Date-range presets → API `since` cutoff (server-side filtering on /eobs/files).
export const RANGES = [
  { id: "today", label: "Today" },
  { id: "2d", label: "Last 2 days" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "all", label: "All time" },
];
export function sinceFor(rangeId) {
  if (rangeId === "today") { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  const hours = { "2d": 48, "7d": 168, "30d": 720 }[rangeId];
  return hours ? new Date(Date.now() - hours * 3600 * 1000).toISOString() : "all";
}

// ── Payer / payee / NPI linkage stats (all-time, distinct names) ──
// Real API fields: file.edi_payer_id (payer linked), file.ehi_client_id (payee EHI
// aligned), file.payee_npi_master || summary.payee_npi (payee NPI ready).
// Display-only for now — the configurator lands later.
export function computeLinkage(files) {
  const payers = new Map(); // name → linked
  const payees = new Map(); // name → { ehi, npi }
  for (const f of files) {
    const s = f.extraction_summary || f.summary || {};
    const payer = (s.payer_name || "").trim();
    const payee = (s.payee_name || "").trim();
    if (payer && payer.toLowerCase() !== "unknown") {
      const linked = !!f.edi_payer_id || !!s.edi_payer_id;
      payers.set(payer, payers.get(payer) || linked);
    }
    if (payee) {
      const cur = payees.get(payee) || { ehi: false, npi: false };
      if (f.ehi_client_id || s.ehi_client_id) cur.ehi = true;
      if (f.payee_npi_master || s.payee_npi) cur.npi = true;
      payees.set(payee, cur);
    }
  }
  const payerList = [...payers.entries()].map(([name, linked]) => ({ name, linked })).sort((a, b) => a.linked - b.linked || a.name.localeCompare(b.name));
  const payeeList = [...payees.entries()].map(([name, v]) => ({ name, ...v })).sort((a, b) => a.name.localeCompare(b.name));
  const count = (arr, k) => arr.filter((x) => x[k]).length;
  return {
    payers: payerList,
    payees: payeeList,
    stats: {
      payersLinked: { ready: count(payerList, "linked"), total: payerList.length },
      payeesAligned: { ready: count(payeeList, "ehi"), total: payeeList.length },
      payeeNpis: { ready: count(payeeList, "npi"), total: payeeList.length },
    },
  };
}

export async function listLinkage({ signal } = {}) {
  const files = await listEobFiles({ since: "all", signal });
  return computeLinkage(files);
}

// Live per-file processing status (stage + message) — only the single-file
// endpoint carries `progress`, so poll it for actively-streaming checks.
export async function fetchCheckProc(fileId) {
  const e = await getEobFile(fileId);
  return {
    status: e.status,
    stage: e.progress?.stage,
    message: e.progress?.message,
    auditStatus: e.audit_status,
    claimCount: e.extraction_summary?.claim_count,
  };
}

// Client-side aggregates for the money cards (no summary API exists).
// Cards are ALWAYS all-time; each carries an aging breakdown (by upload date).
export const AGE_BUCKETS = [
  { id: "d2", label: "≤ 2 days" },
  { id: "d10", label: "2–10 days" },
  { id: "d30", label: "10–30 days" },
  { id: "d30p", label: "30 days +" },
];
const ageDays = (v) => (v ? (Date.now() - new Date(v).getTime()) / 86400000 : Infinity);
const bucketOf = (v) => { const a = ageDays(v); return a <= 2 ? "d2" : a <= 10 ? "d10" : a <= 30 ? "d30" : "d30p"; };
function bucketize(arr, amtKey) {
  const b = { d2: { amt: 0, n: 0 }, d10: { amt: 0, n: 0 }, d30: { amt: 0, n: 0 }, d30p: { amt: 0, n: 0 } };
  for (const c of arr) { const k = bucketOf(c.createdAt); b[k].amt += c[amtKey] || 0; b[k].n += 1; }
  return b;
}
export function aggregates(checks) {
  const sum = (arr, k) => arr.reduce((a, c) => a + (c[k] || 0), 0);
  const unapplied = checks.filter((c) => c.unapplied > 0 && c.postState !== "processing");
  const ready = checks.filter((c) => c.postState === "unapplied");
  const gaps = checks.filter((c) => c.postState === "gap");
  return {
    totalUnapplied: { amt: sum(unapplied, "unapplied"), n: unapplied.length, buckets: bucketize(unapplied, "unapplied") },
    ready: { amt: sum(ready, "amount"), n: ready.length, buckets: bucketize(ready, "amount") },
    gaps: { amt: sum(gaps, "unapplied"), n: gaps.length, buckets: bucketize(gaps, "unapplied") },
  };
}

// ── MOCK: posting rules (no API) ──
export const POSTING_RULES = [
  { id: "pat-resp", on: true, title: "Patient responsibility → statement", sub: "Roll copay / deductible / coins to the patient", tag: "210 claims · $14,600" },
  { id: "ded-carc1", on: true, title: "Deductible to patient · CARC 1", sub: "revQ suggests a look — larger balances", tag: "60 claims · $5,200" },
  { id: "small-bal", on: true, title: "Small-balance write-off", sub: "Auto-adjust balances under $5", tag: "88 claims · $310" },
  { id: "contractual", on: true, title: "Contractual adjustment · CARC 45", sub: "Post plan write-offs automatically", tag: "402 claims · $61,900" },
  { id: "secondary", on: false, title: "Auto-forward to secondary", sub: "Queue a secondary claim when a COB balance remains", tag: "34 claims · $9,120" },
];

export const STATUS_PILLS = [
  { id: "all", label: "All" },
  { id: "unapplied", label: "Un-applied" },
  { id: "applied", label: "Applied" },
  { id: "partial", label: "Partial" },
  { id: "force", label: "Force applied" },
  { id: "gap", label: "Gaps" },
];

export const isToday = (v) => {
  if (!v) return false;
  const d = new Date(v); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

export function filterChecks(checks, f) {
  return checks.filter((c) => {
    if (f.batch && !isToday(c.createdAt)) return false;
    if (f.status && f.status !== "all") {
      if (f.status === "gap" && c.postState !== "gap") return false;
      if (f.status !== "gap" && c.postState !== f.status) return false;
    }
    if (f.payer && !`${c.payer} ${c.ediPayerId}`.toLowerCase().includes(f.payer.toLowerCase())) return false;
    if (f.q) {
      const hay = `${c.payer} ${c.checkNo} ${c.filename} ${c.ediPayerId}`.toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    if (f.amtMin != null && f.amtMin !== "" && c.amount < Number(f.amtMin)) return false;
    if (f.amtMax != null && f.amtMax !== "" && c.amount > Number(f.amtMax)) return false;
    if (f.dateFrom && c.createdAt && new Date(c.createdAt) < new Date(f.dateFrom)) return false;
    if (f.dateTo && c.createdAt && new Date(c.createdAt) > new Date(`${f.dateTo}T23:59:59`)) return false;
    return true;
  });
}
